import { createContext, useContext } from "react";
import type { components } from "@cntrl/api-client";

type NavigationDocument = components["schemas"]["NavigationDocument"];

/** Which chrome shell is mounted. Roster mounts the SAME modules with a different BandContext.*/
export type ShellKind = "artist" | "roster" | "mobile";

/**
 * The BandContext — replaces the MVP's `?embed=1&bandId&as=` query trio. Every feature module
 * renders under this; all API hooks read `bandId` from here, so a module CANNOT fetch outside its tenant.
 * The value comes FROM THE SERVER (the navigation document): the client never computes or selects an
 * effective role (the `?as=` client override dies). This is the load-bearing frontend decision.
 */
export interface BandContextValue {
  bandId: string;
  /** The server-computed effective role — never client-derived.*/
  effectiveRole: string;
  /** Held capability tokens `{token: true}` — the per-screen flags the client keys on.*/
  capabilities: Readonly<Record<string, boolean>>;
  /** The money-capability class: full/per_show/per_person/none.*/
  moneyScope: string;
  /**
   * The band's PRODUCT tier: `"standard"` | `"pro"` (ART-141). Server-resolved, exactly like
   * `effectiveRole` and `moneyScope` — the client never derives it, and in particular never infers it from
   * data it happens to have (crew size, bus count, show count). Read it through {@link useIsPro}.
   */
  planTier: string;
  /**
   * Whether this band's Antenna hub is OPEN — the AC-launch gate (plan §1.3 AC-1), a per-band server fact like
   * `planTier`. The capability set already withholds every `antenna:*` token from a closed band, so this is not
   * the gate; it is the REASON, which lets `/antenna` say "not open for this band yet" instead of a role refusal.
   * It is read ONCE, by the `/antenna` route wrapper (`features/antenna/AntennaHubGate`), never per widget.
   */
  antennaHubEnabled: boolean;
  /**
   * Whether this band's team gets the PRO Advance Docs (ART-353): Pro AND not switched off in Settings › Team.
   * Server-composed, so the client never re-derives tier × choice. Read it through {@link useIsAdvancePro}.
   */
  advanceDocsPro: boolean;
  shell: ShellKind;
  /**
   * The serving org, present ONLY under the roster lens (`shell: 'roster'`). When set, the generated client
   * rewrites band-direct paths to their `/orgs/{orgId}/bands/{bandId}/…` mirror — so feature hooks
   * NEVER choose the prefix, the shell does. Absent (undefined) for the artist/mobile shells.
   */
  orgId?: string;
}

const BandContext = createContext<BandContextValue | null>(null);

export const BandProvider = BandContext.Provider;

/** The active BandContext, or null when no band is selected (e.g. the org-home shell). */
export function useBandContextOrNull(): BandContextValue | null {
  return useContext(BandContext);
}

/** The active BandContext; throws if a band-scoped module mounts without one (a routing bug). */
export function useBandContext(): BandContextValue {
  const ctx = useContext(BandContext);
  if (!ctx) throw new Error("useBandContext() requires a mounted <BandProvider>.");
  return ctx;
}

/** True iff the caller holds the capability token (capabilities-as-data — never a client role matrix).*/
export function useCapability(token: string): boolean {
  const ctx = useContext(BandContext);
  return ctx?.capabilities[token] === true;
}

/**
 * True iff the mounted band is on Artist CNTRL Pro — the ONE client-side Pro predicate.
 *
 * Gate at the SECTION or ROUTE level, one check per surface. Never per-widget, and never by forking a
 * component into Pro and Standard copies: the MVP's own note records what that cost — *"not sprinkled
 * per-widget (the VENUE_KEYS lesson: scattered logic drifts)"*.
 *
 * Where a whole surface is Pro-only, drop it from the nav rather than rendering a control that can only
 * 402 — a tab whose sole outcome is a refusal is a dead button. Where a Standard band should be SOLD the
 * feature, that is a designed upsell state, not an error card.
 *
 * Returns false with no band mounted, which is correct and is also the trap: Pro is a fact about a BAND, so
 * an org-home surface has no tier to read and must ask the server per artist instead.
 */
export function useIsPro(): boolean {
  return isProContext(useContext(BandContext));
}

/**
 * The same predicate for callers OUTSIDE the provider — the shells, which BUILD the BandContext from the
 * navigation document and so cannot consume it. Split out rather than letting each of them write
 * `ctx?.planTier === "pro"`: that is one string comparison per shell, and four copies of a comparison is how
 * `holdCountdown` ended up with three opinions about the same rule.
 */
/**
 * True iff the mounted band's team uses the PRO Advance Docs — a Pro band that has not turned "Advance Docs Pro"
 * off in Settings › Team (ART-353). The advance surfaces read this, never {@link useIsPro}, so the band's choice
 * lands everywhere the Pro advance is shown.
 */
export function useIsAdvancePro(): boolean {
  return useContext(BandContext)?.advanceDocsPro === true;
}

export function isProContext(ctx: BandContextValue | null | undefined): boolean {
  return ctx?.planTier === "pro";
}

/** Build a BandContextValue from the server navigation document + the shell it is mounted in. */
export function bandContextFromNav(nav: NavigationDocument, shell: ShellKind): BandContextValue | null {
  const bandId = nav.context?.bandId;
  if (!bandId) return null;
  return {
    bandId,
    effectiveRole: nav.effectiveRole ?? "",
    capabilities: (nav.capabilities as Record<string, boolean> | undefined) ?? {},
    moneyScope: nav.moneyScope ?? "none",
    // Absent ⇒ standard. The server sends this on every band context; the fallback is for an older
    // document, and it fails CLOSED — an unknown tier is never Pro.
    planTier: nav.planTier ?? "standard",
    // Absent ⇒ closed, for the same reason: an older document must never open a hub the server gated.
    antennaHubEnabled: nav.antennaHubEnabled ?? false,
    // Absent ⇒ off: an older document never shows the Pro advance the server did not compose.
    advanceDocsPro: nav.advanceDocsPro ?? false,
    shell,
    // The org-lens document carries BOTH ids: `{kind:'band', bandId, orgId}`. Only the roster shell
    // needs the orgId (the artist shell's band-direct doc has no orgId). Undefined outside the org lens.
    orgId: shell === "roster" ? (nav.context?.orgId ?? undefined) : undefined,
  };
}
